import React from "react";
import { ArrowRight, MessageCircle, Phone, ShieldCheck, Star, Truck, CheckCircle2 } from "lucide-react";
import { SITE, telLink, waLink } from "@/lib/site";

export const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7 reveal">
        <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">
          <span className="h-px w-8 bg-[#2563EB]" /> Cardiff · Newport · Swansea · South Wales
        </span>
        <h1 data-testid="hero-headline" className="mt-6 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          Professional Packers &amp; Movers, Removals and Deep Cleaning across{" "}
          <span className="relative inline-block">
            <span className="relative z-10">Cardiff</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#2563EB]/15 -z-0" aria-hidden />
          </span>{" "}
          &amp; South Wales.
        </h1>
        <p data-testid="hero-subheadline" className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
          Reliable man &amp; van, house removals, office relocation, professional packing and end of tenancy cleaning. Fully insured, same-day availability, transparent pricing — <span className="font-semibold text-[#0A192F]">removals and cleaning near you</span> in Cardiff, Newport and Swansea.
        </p>

        <div className="mt-9 flex flex-wrap gap-3">
          <a href="#contact" data-testid="hero-quote-btn"
             className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-7 py-4 font-semibold hover:bg-[#0F172A] transition-all hover:shadow-xl hover:-translate-y-0.5">
            Get Free Quote <ArrowRight className="h-4 w-4" />
          </a>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
             className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 font-semibold hover:brightness-95 transition-all hover:-translate-y-0.5">
            <MessageCircle className="h-4 w-4" /> WhatsApp Now
          </a>
          <a href={telLink} data-testid="hero-call-btn"
             className="inline-flex items-center gap-2 rounded-full bg-white border border-[#0A192F] text-[#0A192F] px-7 py-4 font-semibold hover:bg-slate-50 transition-all">
            <Phone className="h-4 w-4" /> Call {SITE.phone}
          </a>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
          <Fact icon={<ShieldCheck className="h-5 w-5 text-[#2563EB]" />} label="Fully Insured" />
          <Fact icon={<Star className="h-5 w-5 text-[#2563EB]" />} label="5-Star Service" />
          <Fact icon={<Truck className="h-5 w-5 text-[#2563EB]" />} label="Same-Day Available" />
        </div>
      </div>

      <div className="lg:col-span-5 reveal reveal-delay-2">
        <div className="relative">
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-3xl bg-[#0A192F]/5 hidden md:block" aria-hidden />
          <div className="absolute -bottom-6 -right-6 h-24 w-24 rounded-3xl bg-[#2563EB]/10 hidden md:block" aria-hidden />
          <div className="relative overflow-hidden rounded-[2rem] h-[420px] md:h-[520px] shadow-[0_20px_60px_rgb(10,25,47,0.25)] border border-slate-200">
            <img
              src="https://images.pexels.com/photos/7464469/pexels-photo-7464469.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="A smiling BoxB customer carrying moving boxes into their new Cardiff home"
              loading="eager"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/30 to-transparent" />

            <div className="relative h-full w-full p-8 md:p-10 flex flex-col justify-end text-white">
              <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-4 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {["a","b","c"].map(k => (
                    <div key={`av-${k}`} className="h-9 w-9 rounded-full bg-white/20 border-2 border-white/30" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-white">
                    {["s1","s2","s3","s4","s5"].map(k => (<Star key={`hero-${k}`} className="h-4 w-4 fill-current" />))}
                    <span className="ml-2 font-display font-bold">4.9</span>
                  </div>
                  <p className="text-xs text-white/80 font-medium">Trusted by 127+ Cardiff &amp; South Wales customers.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Fact = ({ icon, label }) => (
  <div className="flex items-center gap-2">
    {icon}
    <span className="text-sm font-semibold text-[#0A192F]">{label}</span>
  </div>
);
